/**
 * Primeros auxilios — contenido curado de fuentes oficiales (ver sources.ts).
 * CERO invención. Lenguaje español latino, simple. Cifras críticas citadas
 * textualmente de la fuente (RCP 100-120/min · ≥5 cm; atragantamiento 5+5).
 */
import type { Procedure } from "./types";

export const FIRST_AID: Procedure[] = [
  {
    id: "sangrado-abundante",
    title: "Sangrado fuerte (hemorragia)",
    emoji: "🩸",
    summary: "Cómo detener una herida que sangra mucho.",
    steps: [
      "Si puedes, ponte guantes o usa una bolsa de plástico limpia en tu mano para protegerte.",
      "Recuesta a la persona y, si es posible, levanta la parte que sangra por encima del nivel del corazón.",
      "Cubre la herida con una gasa o un trapo limpio y presiona fuerte y directo con la palma de tu mano, sin soltar, hasta que pare el sangrado.",
      "Si la sangre traspasa el trapo, NO lo quites: pon otra gasa o trapo encima y sigue presionando firme.",
      "Cuando pare, deja el vendaje puesto y mantén a la persona quieta y tranquila mientras llega la ayuda.",
    ],
    dont: [
      "No presiones sobre un ojo lastimado ni sobre un objeto clavado.",
      "No saques objetos grandes o clavados profundo (pueden estar tapando la herida y frenando la sangre).",
      "No quites el primer trapo aunque se empape: agrega más encima.",
      "No hurgues dentro de la herida.",
    ],
    callEmergency: [
      "Si la sangre sale a chorros o no para con la presión.",
      "Si la herida es profunda o grande, o hay un objeto clavado.",
      "Si la persona se pone pálida, fría o confundida (signo de shock).",
    ],
    sources: ["mayo-bleeding", "ifrc-2020"],
  },
  {
    id: "hemorragia-masiva-torniquete",
    title: "Sangrado de brazo o pierna que no para: torniquete",
    emoji: "🩹",
    summary:
      "Para sangrado muy fuerte en un brazo o una pierna (aplastamiento, amputación) que NO se detiene apretando la herida. El torniquete salva la vida.",
    steps: [
      "Llama al 911 de una vez (o pide a alguien que llame). Por una pérdida de sangre así una persona puede morir en pocos minutos: actúa rápido.",
      "Primero aprieta la herida: pon un trapo limpio o tu mano directo sobre ella y aprieta fuerte sin soltar. Muchas veces el sangrado para solo con esto.",
      "Si la sangre NO para y es en un brazo o una pierna, ahí sí usa un torniquete. El torniquete es SOLO para brazos y piernas; nunca en el cuello ni en el tronco (pecho o barriga).",
      "Coloca el torniquete unos 5 a 7 cm por ARRIBA de la herida (entre la herida y el corazón), nunca encima de la herida ni sobre una articulación (codo, rodilla, muñeca o tobillo). Si no ves bien la herida, ponlo lo más arriba posible del brazo o la pierna.",
      "Aprieta lo más fuerte que puedas hasta que la sangre PARE. Avisa que va a doler, pero es necesario.",
      "ANOTA LA HORA en que lo pusiste: escríbela en el torniquete o en la piel de la persona (por ejemplo en la frente) para que los rescatistas la vean.",
      "NO lo aflojes ni lo quites. Una vez puesto, debe quedarse así hasta que llegue el personal médico.",
      "Si no tienes un torniquete de verdad, improvisa: usa una tela ancha (al menos 4 cm de ancho, como una tira de camisa) amarrada arriba de la herida, mete un palo firme y tuércelo para apretar hasta que pare la sangre; amarra el palo para que no se suelte.",
      "Mantén a la persona acostada y abrigada mientras llega la ayuda.",
    ],
    dont: [
      "No uses cordón fino, cable, alambre ni cordones de zapato: cortan la piel y no detienen bien la sangre.",
      "No pongas el torniquete en el cuello ni en el tronco: solo sirve en brazos y piernas.",
      "No lo coloques encima de la herida ni sobre una articulación; ponlo más arriba.",
      "No lo aflojes ni lo quites para 'revisar': puede volver a sangrar fuerte.",
      "No pierdas tiempo: si la sangre no para con presión en un brazo o pierna, pon el torniquete sin demora.",
    ],
    callEmergency: [
      "El sangrado es muy fuerte y no para aunque aprietes la herida.",
      "La sangre sale a chorros o empapa rápido la ropa o el trapo.",
      "Un brazo o una pierna quedó aplastado, cortado o arrancado.",
      "La persona se pone pálida, fría, sudorosa, mareada o se desmaya.",
      "Tuviste que poner un torniquete: siempre se llama al 911 después.",
    ],
    sources: ["stop-the-bleed", "redcross-bleeding"],
  },
  {
    id: "rcp-adulto",
    title: "RCP en un adulto (solo con las manos)",
    emoji: "❤️",
    summary: "Si un adulto no responde y no respira normal.",
    steps: [
      'Verifica si responde: sacúdele el hombro y grítale "¿estás bien?". Si no responde y no respira normal (o solo jadea), actúa.',
      "Llama YA al 911 (o pide a alguien que llame) y consigue un desfibrilador (DEA) si hay uno cerca.",
      "Arrodíllate al lado de la persona, que esté boca arriba sobre una superficie firme.",
      "Coloca el talón de una mano en el CENTRO del pecho (sobre el esternón) y la otra mano encima, entrelazando los dedos.",
      "Con los brazos rectos, empuja FUERTE y RÁPIDO hacia abajo: al menos 5 cm de profundidad (sin pasar de 6 cm), a un ritmo de 100 a 120 compresiones por minuto.",
      "Deja que el pecho suba completo entre cada compresión (no te apoyes encima).",
      "No pares. Sigue hasta que llegue la ayuda, aparezca un DEA, o la persona despierte o respire.",
    ],
    dont: [
      "No te detengas a revisar el pulso una y otra vez: si no responde y no respira normal, comprime.",
      "No comprimas suave ni lento: debe ser fuerte y rápido.",
      "Si no tienes entrenamiento, no es necesario dar respiraciones boca a boca: solo manos.",
    ],
    callEmergency: [
      "Siempre, ANTES de empezar, o que alguien llame al 911 mientras tú comprimes.",
    ],
    sources: ["aha-cpr", "ifrc-2020"],
  },
  {
    id: "rcp-nino-bebe",
    title: "RCP en niño y bebé",
    emoji: "🧒",
    summary:
      "RCP para un niño (1 año hasta la pubertad) o un bebé (menos de 1 año) que no responde y no respira normal. En ellos casi siempre falta el aire, por eso SÍ se dan respiraciones.",
    steps: [
      "Mira si reacciona: a un niño tócale el hombro y llámalo fuerte; a un bebé dale golpecitos suaves en la planta del pie (nunca lo sacudas). Si no se mueve, no llora y no respira (o solo da boqueadas raras), necesita RCP ya.",
      "Pide ayuda y llama al 911. Si hay alguien contigo, mírale a los ojos y dile: 'Tú, llama al 911 ahora y busca un desfibrilador'. Pon el teléfono en altavoz para que te guíen.",
      "Si estás solo y NO viste cuando se desmayó, da primero 2 minutos de RCP y recién después llama al 911 (en niños y bebés casi siempre falla primero la respiración).",
      "Acuéstalo boca arriba sobre algo firme. En un bebé, cuídale siempre la cabeza y el cuello.",
      "Coloca las manos. NIÑO: el talón de UNA mano en el centro del pecho (o las DOS, una sobre otra, si te cuesta hundir bien). BEBÉ: lo mejor es rodear el pecho con las dos manos y apretar con los dos PULGARES en el centro, justo debajo de la línea de los pezones; si no logras rodearlo, usa dos dedos en ese mismo punto.",
      "Aprieta fuerte y rápido hacia abajo. NIÑO: hunde el pecho unos 5 cm (como un tercio del grosor del pecho). BEBÉ: hunde unos 4 cm (también un tercio). Deja que el pecho suba del todo entre cada apretón.",
      "Lleva un ritmo de 100 a 120 apretones por minuto (rápido, como 2 por segundo).",
      "Haz 30 apretones y luego 2 respiraciones de rescate: en el NIÑO inclínale un poco la cabeza, tápale la nariz y sopla por la boca; en el BEBÉ cubre con tu boca su boca y nariz a la vez. Cada soplo dura como 1 segundo, solo hasta ver que el pecho sube un poco.",
      "Sigue sin parar repitiendo el ciclo: 30 apretones y 2 respiraciones, una y otra vez.",
      "No pares hasta que llegue ayuda o un desfibrilador, el niño se mueva o respire, o estés agotado. Si llega un desfibrilador, enciéndelo y sigue lo que la voz indique.",
    ],
    dont: [
      "No soples muy fuerte ni le llenes el pecho de aire: solo hasta que suba un poco. Soplar de más le hace daño.",
      "No aprietes demasiado suave por miedo: el pecho debe hundirse de verdad (un tercio del grosor).",
      "No dejes de apretar largo rato para revisarlo: cada pausa le quita oxígeno. Solo lo justo para dar las 2 respiraciones.",
      "En un bebé no uses la fuerza de las dos manos como en un adulto: usa los dos pulgares rodeando el pecho (o, si no puedes rodearlo, dos dedos).",
      "No pierdas tiempo buscando el pulso si no sabes: si no responde y no respira normal, empieza la RCP.",
    ],
    callEmergency: [
      "Llama al 911 apenas veas que un niño o bebé no responde y no respira normal (o solo da boqueadas).",
      "Si hay otra persona, que llame al 911 de inmediato mientras tú empiezas la RCP sin perder tiempo.",
      "Si estás solo y NO viste el desmayo: da 2 minutos de RCP y enseguida llama al 911.",
      "Pon el teléfono en altavoz: en el 911 te pueden guiar paso a paso mientras das RCP.",
    ],
    sources: ["aha-pediatric", "redcross-child-cpr", "ifrc-2020"],
  },
  {
    id: "atragantamiento-adulto",
    title: "Atragantamiento en un adulto",
    emoji: "😶",
    summary: "Si un adulto se ahoga y no puede hablar, toser ni respirar.",
    steps: [
      'Pregunta "¿te estás ahogando?". Si no puede hablar, toser ni respirar, o se agarra el cuello, actúa.',
      "Ponte a un lado y un poco detrás. Inclínalo hacia adelante.",
      "Da 5 golpes firmes en la espalda, entre los omóplatos, con el talón de tu mano.",
      "Si no sale el objeto, ponte detrás y rodéale la cintura con tus brazos.",
      "Cierra un puño y ponlo con el lado del pulgar contra la barriga, un poco arriba del ombligo. Tómalo con la otra mano.",
      "Aprieta la barriga con 5 compresiones rápidas hacia ADENTRO y hacia ARRIBA (esto se llama maniobra de Heimlich).",
      "Alterna ciclos de 5 golpes en la espalda + 5 compresiones en la barriga, hasta que salga el objeto o la persona pueda toser o hablar.",
      "Si la persona se desmaya, bájala con cuidado a una superficie firme y empieza RCP (compresiones).",
    ],
    dont: [
      "No metas los dedos a ciegas en la boca para sacar el objeto (puedes empujarlo más adentro).",
      "No le des golpes en la espalda sin inclinarlo hacia adelante.",
      "No le sigas dando agua ni comida.",
    ],
    callEmergency: [
      "Llama al 911 apenas empieza el atragantamiento serio (o pide que alguien llame mientras tú ayudas).",
      "Si se desmaya, es urgente.",
    ],
    sources: ["redcross-choking-adult", "ifrc-2020"],
  },
  {
    id: "atragantamiento-bebe",
    title: "Atragantamiento en un bebé (menor de 1 año)",
    emoji: "👶",
    summary: "Un bebé que se ahoga se atiende distinto a un adulto.",
    steps: [
      "Si el bebé no puede toser, llorar ni respirar, actúa de inmediato.",
      "Acuesta al bebé boca abajo sobre tu antebrazo, apoyando el brazo en tu muslo. La cabeza debe quedar MÁS BAJA que el cuerpo. Sostén la cabeza y la mandíbula con tu mano.",
      "Da 5 golpes firmes en la espalda, entre los omóplatos, con el talón de tu mano.",
      "Voltea al bebé boca arriba, con la cabeza más baja que el cuerpo.",
      "Da 5 compresiones en el pecho: pon 2 o 3 dedos en el centro del pecho, en la línea entre los pezones, y empuja hacia abajo unos 4 cm (1½ pulgadas).",
      "Alterna 5 golpes en la espalda + 5 compresiones en el pecho hasta que salga el objeto o el bebé tosa o llore.",
      "Si el bebé deja de responder, bájalo a una superficie firme y empieza RCP infantil (compresiones).",
    ],
    dont: [
      "NUNCA hagas compresiones en la barriga (Heimlich) a un bebé: pueden dañarle el hígado.",
      "No metas los dedos a ciegas en su boca para buscar el objeto.",
      "No lo sostengas con la cabeza más alta que el cuerpo durante los golpes.",
    ],
    callEmergency: [
      "Llama al 911 de inmediato (o pide que alguien llame mientras tú ayudas al bebé).",
    ],
    sources: ["redcross-choking-infant", "ifrc-2020"],
  },
  {
    id: "quemaduras",
    title: "Quemaduras",
    emoji: "🔥",
    summary: "Cómo enfriar y proteger una quemadura.",
    steps: [
      "Aleja a la persona de la fuente de calor y, si es seguro, quita anillos, pulseras o ropa apretada del área antes de que se hinche (pero NO la ropa pegada a la quemadura).",
      "Enfría la quemadura con agua de la llave FRESCA (no helada) corriendo sobre el área por unos 10 minutos.",
      "Cubre la quemadura con un vendaje o tela limpia, floja, para protegerla y aliviar el dolor.",
      "Si hay dolor, la persona puede tomar un analgésico común de venta libre.",
    ],
    dont: [
      "No uses hielo ni agua helada directo sobre la quemadura (daña más la piel).",
      "No pongas mantequilla, pasta de dientes, aceite ni cremas grasosas (atrapan el calor y aumentan el riesgo de infección).",
      "No revientes las ampollas.",
      "No quites la ropa que quedó pegada a la piel quemada.",
    ],
    callEmergency: [
      "Si la quemadura es grande o profunda.",
      "Si afecta cara, manos, pies, genitales o una articulación grande.",
      "Si es por fuego, electricidad o químicos.",
      "Si la piel se ve blanca, acartonada, o quemada en círculo alrededor de un brazo o pierna.",
    ],
    sources: ["mayo-burns", "ifrc-2020"],
  },
  {
    id: "fracturas",
    title: "Fractura o hueso roto",
    emoji: "🦴",
    summary: "Si sospechas que un hueso se quebró.",
    steps: [
      "No muevas a la persona a menos que sea estrictamente necesario (peligro cercano).",
      "Si hay sangrado, presiona con una gasa o tela limpia hasta que pare.",
      "Inmoviliza la zona lesionada: mantenla quieta en la posición en que está.",
      "Si sabes entablillar y la ayuda tardará, coloca una férula (tablilla) sujetando por arriba y por abajo de la zona quebrada.",
      "Pon hielo envuelto en una tela (nunca directo sobre la piel) para bajar la hinchazón y el dolor.",
      "Vigila signos de shock: si se siente débil o respira corto y rápido, acuéstala y, si puedes, levántale las piernas.",
    ],
    dont: [
      "No intentes acomodar el hueso ni empujar hacia adentro un hueso que sobresale.",
      "No muevas a la persona si sospechas lesión de columna (cuello o espalda).",
      "No pongas hielo directo sobre la piel.",
    ],
    callEmergency: [
      "Si el hueso atravesó la piel o hay sangrado fuerte.",
      "Si fue por un golpe o accidente grande, o sospechas lesión de cuello o espalda.",
      "Si la persona no responde o no respira.",
      "Si la zona se ve azulada o sin pulso ni sensibilidad.",
    ],
    sources: ["mayo-fractures", "ifrc-2020"],
  },
  {
    id: "aplastamiento",
    title: "Persona atrapada o aplastada",
    emoji: "🧱",
    summary:
      "Muy importante tras un derrumbe: cómo ayudar sin causar más daño.",
    steps: [
      "Asegura primero tu seguridad: no entres a una estructura inestable; pide ayuda de rescate profesional.",
      "Si la persona pudo lesionarse el cuello o la espalda, NO la muevas. Mantenle la cabeza y el cuello quietos: sostén con tus manos o pon toallas o ropa enrolladas a ambos lados del cuello.",
      "Habla con la persona, mantenla consciente y tranquila mientras llega el rescate.",
      "Si tiene sangrado visible, presiona con tela limpia.",
      "ADVERTENCIA: si una parte del cuerpo lleva mucho rato aplastada (varias horas), liberar el peso de golpe puede ser muy peligroso (síndrome de aplastamiento). NO retires el objeto pesado sin la guía del personal de rescate o médico.",
      "Si está consciente y NO sospechas lesión de columna ni de pelvis, puedes recostarla y levantarle un poco las piernas.",
    ],
    dont: [
      "No muevas ni jales a la persona si sospechas lesión de columna.",
      "No liberes de golpe el peso que aplasta tras un atrapamiento prolongado, sin guía del rescate.",
      "No le tuerzas la cabeza ni el cuello.",
      "Si hay que dar RCP con sospecha de lesión de cuello, no le inclines la cabeza hacia atrás para abrir la vía aérea.",
    ],
    callEmergency: [
      "Siempre, de inmediato. Es una emergencia que requiere rescate y atención médica urgente, sobre todo si lleva atrapada más de unos minutos.",
    ],
    sources: ["cdc-crush", "mayo-spinal"],
  },
  {
    id: "shock",
    title: "Shock (gravedad por mala circulación)",
    emoji: "🫥",
    summary:
      "Estado peligroso que puede seguir a una herida grave, quemadura o pérdida de sangre.",
    steps: [
      "Reconoce los signos: piel fría, sudorosa, pálida o grisácea; labios o uñas azulados; pulso rápido; respiración rápida y superficial; debilidad, náuseas o confusión.",
      "Llama al 911.",
      "Recuesta a la persona boca arriba y levántale las piernas un poco (unos 30 cm), salvo que eso le cause dolor o haya una lesión que lo impida.",
      "Mantenla quieta y cómoda; afloja la ropa apretada.",
      "Abrígala con una manta o ropa para que no pierda calor (cubre cabeza, manos y pies).",
    ],
    dont: [
      "No le des de comer ni de tomar nada.",
      "No le levantes las piernas si sospechas lesión de columna, pelvis o pierna, o si le causa dolor o le cuesta respirar.",
      "No la dejes sola.",
    ],
    callEmergency: [
      "Siempre. El shock es una emergencia que pone en peligro la vida.",
    ],
    sources: ["mayo-shock", "ifrc-2020"],
  },
  {
    id: "desmayo-inconsciencia",
    title: "Desmayo o persona inconsciente que respira",
    emoji: "😵",
    summary:
      "Cómo poner a alguien de costado (posición de recuperación) para que no se ahogue.",
    steps: [
      "Si la persona está inconsciente pero RESPIRA y no sospechas lesión de cuello o espalda, ponla de costado para que la vía aérea quede abierta y, si vomita, no se ahogue.",
      "Arrodíllate a su lado. Coloca el brazo más cercano a ti en ángulo recto, con el codo doblado y la palma hacia arriba.",
      "Pasa el otro brazo cruzando el pecho y apoya el dorso de su mano contra la mejilla del lado tuyo.",
      "Dobla la rodilla más lejana hasta que el pie quede apoyado en el piso. Tira de esa rodilla para girar a la persona hacia ti, sobre su costado.",
      "Inclina suavemente la cabeza hacia atrás y levanta el mentón para mantener la vía aérea abierta. Vigila que siga respirando.",
      "Si solo se desmayó y sigue boca arriba sin lesión: acuéstalo, levántale las piernas unos 30 cm y afloja la ropa apretada.",
    ],
    dont: [
      "No la sientes ni la levantes de golpe (puede volver a desmayarse).",
      "No la pongas de costado si sospechas lesión de columna; en ese caso mantenla quieta como está.",
      "No le des de comer ni de tomar hasta que esté totalmente recuperada.",
    ],
    callEmergency: [
      "Si no recobra el conocimiento en 1 minuto.",
      "Si no respira (empieza RCP).",
      "Si se golpeó la cabeza, tiene dolor de pecho, dificultad para respirar, convulsiones, o se desmaya repetidas veces.",
    ],
    sources: ["sja-recovery", "mayo-fainting"],
  },
  {
    id: "golpe-de-calor",
    title: "Golpe de calor / deshidratación",
    emoji: "🥵",
    summary: "Cuando el cuerpo se sobrecalienta: es una emergencia.",
    steps: [
      "Reconoce el golpe de calor: temperatura muy alta (40 °C o más), piel caliente, confusión o habla enredada, pulso y respiración rápidos, náuseas.",
      "Llama al 911 de inmediato.",
      "Saca a la persona del calor y llévala a la sombra o a un lugar fresco.",
      "Enfríala por cualquier medio: ponla bajo agua fresca, mójala, pásale paños con agua fresca, abanícala, o pon paños húmedos en cuello, axilas e ingle.",
      "Si está consciente y puede tragar, dale agua fresca o una bebida con electrolitos (sin alcohol ni cafeína).",
    ],
    dont: [
      "No le des líquidos si está inconsciente o muy confundida (riesgo de ahogarse).",
      "No le des bebidas con alcohol ni cafeína.",
      'No esperes a que "se le pase": el golpe de calor es una emergencia médica.',
    ],
    callEmergency: [
      "Siempre, ante sospecha de golpe de calor (fiebre alta + confusión o pérdida de conocimiento).",
    ],
    sources: ["mayo-heatstroke"],
  },
  {
    id: "convulsiones",
    title: "Convulsión o ataque",
    emoji: "🧠",
    summary:
      "Qué hacer mientras alguien convulsiona (se cae, se sacude o se pone rígido sin control).",
    steps: [
      "Mantén la calma y quédate con la persona todo el tiempo.",
      "Mira la hora y empieza a contar cuánto dura: si pasa de 5 minutos, hay que llamar al 911.",
      "Aleja cosas peligrosas que tenga cerca (sillas, mesas, objetos con punta o filo) para que no se golpee.",
      "Pon algo blando y plano debajo de su cabeza, como ropa doblada, para que no se lastime contra el piso.",
      "Afloja lo que tenga apretado en el cuello (camisa, corbata) y, si usa lentes, quítaselos.",
      "Cuando deje de convulsionar, voltéala con cuidado de lado, con la boca apuntando hacia el piso, para que la saliva salga sola y no se ahogue.",
      "Quédate a su lado mientras despierta: va a estar confundida o cansada. Háblale con calma y explícale qué pasó.",
    ],
    dont: [
      "NO le metas nada en la boca (ni dedos, ni cuchara, ni agua): la persona NO se traga la lengua, y meterle algo le rompe los dientes o la hace atragantarse.",
      "NO la sujetes ni trates de frenar sus movimientos a la fuerza: puede lastimarla a ella o a ti.",
      "NO le des agua ni comida hasta que esté totalmente despierta.",
      "NO le des respiración boca a boca durante el ataque: lo normal es que vuelva a respirar sola al terminar.",
      "NO la dejes sola hasta que esté consciente.",
    ],
    callEmergency: [
      "El ataque dura más de 5 minutos.",
      "Le viene otro ataque enseguida.",
      "No despierta o le cuesta respirar después.",
      "Se golpeó o se lastimó durante el ataque.",
      "El ataque ocurrió dentro del agua.",
      "Es la primera vez que esa persona convulsiona.",
      "Está embarazada, o tiene diabetes y pierde el conocimiento.",
    ],
    sources: ["cdc-seizure", "epilepsy-foundation", "redcross-seizure"],
  },
  {
    id: "electrocucion",
    title: "Descarga eléctrica o cable de luz caído",
    emoji: "⚡",
    summary:
      "Qué hacer si alguien recibe una descarga, o si hay un cable de alta tensión caído tras el sismo.",
    steps: [
      "PRIMERO TU SEGURIDAD: no toques a la persona si todavía está pegada a la corriente (a un cable, un aparato o agua con electricidad). Si la tocas, la corriente también pasa a tu cuerpo.",
      "Si puedes hacerlo sin peligro, corta la electricidad: baja el breaker (la llave o el tablero principal de la luz) o desconecta el aparato del enchufe.",
      "Si NO puedes cortar la luz y la persona sigue pegada a la corriente de un aparato del hogar, empújala lejos de la fuente con algo SECO que no pase electricidad (un palo de madera seco, un cartón grueso, un objeto de plástico). Nunca uses algo metálico ni mojado.",
      "Si es un CABLE DE ALTA TENSIÓN caído en la calle: aléjate al menos 11 metros (unos 35 pasos largos), y mucho más si los cables saltan o echan chispas. El suelo alrededor puede tener corriente.",
      "Da por seguro que TODO cable caído tiene corriente, aunque se vea apagado. No lo toques, no lo muevas con ningún palo, y no toques rejas, cercas, agua ni a alguien que esté pegado al cable.",
      "Llama de inmediato a las autoridades (bomberos, la compañía de electricidad o el 911) para que corten la corriente. Solo personal entrenado debe manejar un cable caído.",
      "Si vas en carro y un cable cae sobre tu vehículo, quédate adentro: no te bajes. Toca la corneta para pedir ayuda y diles a los demás que no se acerquen.",
      "Cuando ya sea seguro, revisa si la persona respira. Si no respira y no se mueve, empieza RCP (compresiones fuertes y rápidas en el centro del pecho) y no pares hasta que llegue ayuda.",
      "Cubre las quemaduras (por donde entró y salió la corriente) con una gasa o un paño limpio. No uses cobija ni toalla: sueltan pelusa que se pega.",
      "Toda persona que recibió una descarga debe ser revisada por un médico, aunque la quemadura se vea pequeña: el daño por dentro puede ser peor de lo que se ve.",
    ],
    dont: [
      "No toques a la persona mientras siga pegada a la corriente.",
      "No te acerques ni toques un cable de alta tensión caído, aunque parezca apagado.",
      "No intentes mover un cable de la calle con un palo, escoba ni ningún objeto: la madera o tela mojada también pasan electricidad.",
      "No te bajes del carro si un cable cayó encima; quédate adentro y pide ayuda.",
      "No pongas cobija ni toalla sobre las quemaduras.",
      "No des por sana a la persona aunque se vea bien: necesita revisión médica.",
    ],
    callEmergency: [
      "Hay un cable de alta tensión caído (llama también a la compañía de electricidad).",
      "La persona no respira, no tiene pulso o se desmayó.",
      "Tiene quemaduras graves, le cuesta respirar, o el corazón le late raro.",
      "Tiene convulsiones, dolores fuertes de músculos, o está confundida.",
    ],
    sources: ["mayo-electrical", "esfi-powerlines"],
  },
  {
    id: "apoyo-psicologico",
    title: "Calmar y acompañar a alguien angustiado",
    emoji: "🫂",
    summary:
      "Primeros auxilios psicológicos: cómo acompañar a una persona en shock, con pánico o muy angustiada tras el desastre. El método es observar, escuchar y conectar.",
    steps: [
      "OBSERVA antes de acercarte: fíjate si el lugar es seguro (estructuras a punto de caer, cables, fuego) y quién está más afectado (personas que tiemblan, lloran sin parar o se quedan ausentes y calladas). Acércate solo si es seguro para ti.",
      "Acércate con calma: si tú estás tranquilo, ayudas a que la otra persona se tranquilice. Ve despacio, con voz suave y frases cortas. Ponte a su altura (si está sentada en el piso, agáchate a su lado).",
      "Preséntate y ofrece ayuda: di tu nombre y que estás ahí para ayudar. Si no quiere ayuda, no la obligues; dile que puede pedirla después.",
      "Llévala a un lugar más seguro y tranquilo, lejos del peligro y, si se puede, del ruido y la multitud. Si tiene frío o está mojada, abrígala.",
      "ESCUCHA sin presionar: acompañar no es obligar a hablar. Quédate a su lado y pon atención. Si quiere hablar, escúchala; si prefiere callar, respeta el silencio. No le pidas que cuente lo que vivió.",
      "Valida lo que siente: 'entiendo que esto da mucho miedo', 'cualquiera se sentiría así'. No juzgues su reacción aunque sea distinta a la tuya.",
      "Si tiene pánico o le cuesta respirar, ayúdala a respirar lento: que tome aire por la nariz despacio sintiendo cómo se le llena la barriga, y que lo bote despacio por la boca. Recuérdale que está a salvo y que el malestar va a ir pasando.",
      "Si está muy ida o bloqueada, tráela al presente: con voz calmada pídele que mire alrededor y te diga qué ve, qué oye y qué siente ahora.",
      "CONECTA con lo que necesita: agua, un lugar donde sentarse, abrigo, o ayuda para contactar a sus seres queridos. Dale solo información cierta; no inventes.",
      "Quédate hasta que esté más calmada o llegue ayuda. Cuando te tengas que ir, dilo claro, despídete con respeto y, si puedes, dile a dónde puede pedir más ayuda.",
    ],
    dont: [
      "NO la presiones a contar lo que vivió ni a 'desahogarse'.",
      "NO minimices lo que siente. Evita 'no fue nada', 'no llores', 'ya pasó', 'tienes que ser fuerte'.",
      "NO hagas promesas que no puedes cumplir ni des información falsa ('todo va a estar bien', 'voy a encontrar a tu familiar').",
      "NO la toques sin permiso (abrazo, mano en el hombro): pregunta antes.",
      "NO la dejes sola si dice que quiere hacerse daño: quédate con ella y busca ayuda de inmediato.",
    ],
    callEmergency: [
      "Tiene dolor en el pecho, le falta el aire o se desmaya: puede ser del corazón, no solo nervios.",
      "Tiene un ataque de pánico y los síntomas físicos (corazón acelerado, falta de aire) no ceden tras unos minutos: busca ayuda médica de inmediato.",
      "Dice que quiere hacerse daño o quitarse la vida: no la dejes sola y busca ayuda urgente.",
      "Se pone violenta y hay riesgo de que se lastime o lastime a otros.",
      "Días o semanas después la angustia sigue tan fuerte que no la deja dormir, comer ni hacer su vida: busca ayuda profesional de salud mental.",
    ],
    sources: ["who-pfa", "ifrc-pfa", "paho-pfa"],
  },
];
